import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { isJsonParsable } from "../helpers";

const PARAM_KEYS = {
  daily: { filter: "drmDailyFilters", ui: "drmDailyUi" },
  bid: { filter: "drmBidFilters", ui: "drmBidUi" },
  ceaProfile: { filter: "ceaProfileFilters", ui: "ceaProfileUi" },
  coupa: { filter: "coupaFilters", ui: "coupaUi" },
};

const COUNTRY_TAGS = {
  ALL: "ALL",
  NORTH_AMERICA: "NORTH_AMERICA",
  NON_NORTH_AMERICA: "NON_NORTH_AMERICA",
};

const DEFAULT_NORTH_AMERICA = ["UNITED STATES", "CANADA", "MEXICO"];

const TAG_KEY = "__tag";
const MAX_URL_PARAM_LENGTH = 1800;

const stringifySafe = (value) => {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return "";
  }
};

const cloneDeep = (value) => {
  try {
    return structuredClone(value);
  } catch (error) {
    return value;
  }
};

const isEmptyValue = (val) =>
  val === undefined || val === null || val === "" ||
  (Array.isArray(val) && val.length === 0);

const pruneEmpty = (value) => {
  if (Array.isArray(value)) {
    const prunedArray = value
      .map((item) => pruneEmpty(item))
      .filter((item) => !isEmptyValue(item) && !(typeof item === "object" && Object.keys(item).length === 0));
    return prunedArray;
  }

  if (value && typeof value === "object") {
    const prunedObject = Object.entries(value).reduce((acc, [key, val]) => {
      const prunedVal = pruneEmpty(val);
      const isObjectAndEmpty = typeof prunedVal === "object" && prunedVal !== null && Object.keys(prunedVal).length === 0;
      if (isEmptyValue(prunedVal) || isObjectAndEmpty) {
        return acc;
      }
      acc[key] = prunedVal;
      return acc;
    }, {});
    return prunedObject;
  }

  return value;
};

const wrapTagValue = (tag) => ({ [TAG_KEY]: tag });

const readTagValue = (value) => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && TAG_KEY in value) {
    return value[TAG_KEY];
  }
  return null;
};

const buildCountryMeta = (countryOptions = [], northAmericaCountries = DEFAULT_NORTH_AMERICA) => {
  const codeToOption = new Map();
  const allCodes = [];
  const northAmericaNames = new Set(northAmericaCountries);
  const northAmericaCodes = [];

  countryOptions.forEach((opt) => {
    const code = opt?.countryCode;
    if (!code) return;
    codeToOption.set(code, opt);
    allCodes.push(code);
    if (northAmericaNames.has(opt?.name)) {
      northAmericaCodes.push(code);
    }
  });

  const northAmericaCodeSet = new Set(northAmericaCodes);
  const nonNorthAmericaCodes = allCodes.filter((code) => !northAmericaCodeSet.has(code));

  return {
    allCodes,
    northAmericaCodes,
    nonNorthAmericaCodes,
    codeToOption,
  };
};

const matchesEveryCode = (codes = [], targetCodes = []) => {
  if (codes.length !== targetCodes.length) return false;
  const codeSet = new Set(codes);
  return targetCodes.every((code) => codeSet.has(code));
};

const compressCountryCodes = (codes = [], countryMeta) => {
  if (!countryMeta || !Array.isArray(codes) || codes.length === 0) {
    return { tag: null, codes };
  }

  if (
    countryMeta.allCodes.length > 0 &&
    matchesEveryCode(codes, countryMeta.allCodes)
  ) {
    return { tag: COUNTRY_TAGS.ALL, codes: [] };
  }

  if (
    countryMeta.northAmericaCodes.length > 0 &&
    matchesEveryCode(codes, countryMeta.northAmericaCodes)
  ) {
    return { tag: COUNTRY_TAGS.NORTH_AMERICA, codes: [] };
  }

  if (
    countryMeta.nonNorthAmericaCodes.length > 0 &&
    matchesEveryCode(codes, countryMeta.nonNorthAmericaCodes)
  ) {
    return { tag: COUNTRY_TAGS.NON_NORTH_AMERICA, codes: [] };
  }

  return { tag: null, codes };
};

const expandCodesFromTag = (tag, countryMeta) => {
  if (!tag || !countryMeta) return [];
  switch (tag) {
    case COUNTRY_TAGS.ALL:
      return countryMeta.allCodes || [];
    case COUNTRY_TAGS.NORTH_AMERICA:
      return countryMeta.northAmericaCodes || [];
    case COUNTRY_TAGS.NON_NORTH_AMERICA:
      return countryMeta.nonNorthAmericaCodes || [];
    default:
      return [];
  }
};

const expandOptionsFromTag = (tag, countryMeta) => {
  if (!tag || !countryMeta?.codeToOption) return [];
  const codes = expandCodesFromTag(tag, countryMeta);
  return codes
    .map((code) => countryMeta.codeToOption.get(code))
    .filter(Boolean);
};

const compressFilterForUrl = (filterState, countryMeta, scope) => {
  if (!filterState || !countryMeta) return filterState;

  const next = { ...filterState };
  const originKey = scope === "daily" ? "originCountries" : "originCountry";
  const destKey = scope === "daily" ? "destinationCountries" : "destinationCountry";

  [originKey, destKey].forEach((key) => {
    const value = filterState[key];
    if (!Array.isArray(value)) return;
    const { tag, codes } = compressCountryCodes(value, countryMeta);
    next[key] = tag ? wrapTagValue(tag) : codes;
  });

  return pruneEmpty(next);
};

const compressUiForUrl = (uiState, countryMeta) => {
  if (!uiState || !countryMeta) return uiState;
  const next = { ...uiState };

  ["OCountry", "DCountry"].forEach((key) => {
    const value = uiState[key];
    const codes = Array.isArray(value)
      ? value.map((item) => item?.countryCode).filter(Boolean)
      : [];
    if (codes.length === 0) return;
    const { tag } = compressCountryCodes(codes, countryMeta);
    if (tag) {
      next[key] = wrapTagValue(tag);
    }
  });

  return next;
};

const expandFilterFromUrl = (parsedFilter, countryMeta, scope) => {
  if (!parsedFilter) return parsedFilter;

  const next = { ...parsedFilter };
  const originKey = scope === "daily" ? "originCountries" : "originCountry";
  const destKey = scope === "daily" ? "destinationCountries" : "destinationCountry";

  [originKey, destKey].forEach((key) => {
    const tag = readTagValue(parsedFilter[key]);
    if (tag) {
      next[key] = expandCodesFromTag(tag, countryMeta);
    }
  });

  return next;
};

const mergeWithDefaults = (defaultFilterState, incomingFilter) => {
  if (!defaultFilterState) return incomingFilter;
  const merged = { ...defaultFilterState, ...incomingFilter };
  if (defaultFilterState.filters || incomingFilter?.filters) {
    merged.filters = {
      ...(defaultFilterState.filters || {}),
      ...(incomingFilter?.filters || {}),
    };
  }
  return merged;
};

const enforceLengthLimit = (serializedValue) => {
  if (!serializedValue) return null;
  return serializedValue.length <= MAX_URL_PARAM_LENGTH ? serializedValue : null;
};

const syncParamToUrl = (params, paramKey, state, serializedValue, defaultRef, lastApplied, appliedKey) => {
  if (!state) return false;

  const targetValue =
    serializedValue && serializedValue !== defaultRef.current
      ? enforceLengthLimit(serializedValue)
      : null;
  const currentValue = params.get(paramKey);

  if (targetValue) {
    if (currentValue === targetValue) return false;
    params.set(paramKey, targetValue);
    lastApplied.current[appliedKey] = targetValue;
    return true;
  }

  if (!currentValue) return false;

  params.delete(paramKey);
  if (lastApplied.current[appliedKey]) {
    lastApplied.current[appliedKey] = "";
  }
  return true;
};

const expandUiFromUrl = (parsedUi, countryMeta) => {
  if (!parsedUi) return parsedUi;
  const next = { ...parsedUi };

  ["OCountry", "DCountry"].forEach((key) => {
    const tag = readTagValue(parsedUi[key]);
    if (tag) {
      next[key] = expandOptionsFromTag(tag, countryMeta);
    }
  });

  return next;
};

const hydrateParamFromUrl = (params, paramKey, lastApplied, appliedKey, isHydrated, parseFn, resetFn) => {
  const urlValue = params.get(paramKey);
  if (urlValue) {
    if (urlValue !== lastApplied.current[appliedKey]) {
      parseFn(urlValue);
      lastApplied.current[appliedKey] = urlValue;
    }
  } else if (lastApplied.current[appliedKey] && isHydrated) {
    resetFn();
    lastApplied.current[appliedKey] = "";
  }
};

export const useDrmFilterUrlSync = ({
  scope,
  filterState,
  dispatch,
  setFilterAction,
  uiState,
  setUiState,
  initialUiState,
  defaultFilterState,
  countryOptions = [],
  northAmericaCountries = DEFAULT_NORTH_AMERICA,
}) => {
  const paramConfig = PARAM_KEYS[scope];
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [isHydrated, setIsHydrated] = useState(false);
  const lastAppliedRef = useRef({ filter: "", ui: "" });

  const pendingWriteRef = useRef(null);
  const isStaleRender = (loc) => {
    const pending = pendingWriteRef.current;
    if (!pending) return false;
    if (loc.key === pending.key && loc.search === pending.search) return true;
    pendingWriteRef.current = null;
    return false;
  };
  
  const countryMeta = useMemo(
    () => buildCountryMeta(countryOptions, northAmericaCountries),
    [countryOptions, northAmericaCountries]
  );
  const isCountryMetaReady = countryOptions.length === 0 || countryMeta?.allCodes?.length > 0;

  const defaultFilterStringRef = useRef(
    stringifySafe(compressFilterForUrl(defaultFilterState, countryMeta, scope))
  );
  const defaultUiStringRef = useRef(stringifySafe(initialUiState));

  useEffect(() => {
    defaultFilterStringRef.current = stringifySafe(
      compressFilterForUrl(defaultFilterState, countryMeta, scope)
    );
  }, [defaultFilterState, countryMeta, scope]);

  useEffect(() => {
    defaultUiStringRef.current = stringifySafe(initialUiState);
  }, [initialUiState]);

  useEffect(() => {
    if (!paramConfig || !isCountryMetaReady) return;

    if (isStaleRender(location)) return;

    const params = new URLSearchParams(location.search);

    hydrateParamFromUrl(
      params, paramConfig.filter, lastAppliedRef, "filter", isHydrated,
      (urlFilter) => {
        const parsedFilter = isJsonParsable(urlFilter);
        if (parsedFilter) {
          const expandedFilter = expandFilterFromUrl(parsedFilter, countryMeta, scope);
          dispatch(setFilterAction(mergeWithDefaults(defaultFilterState, expandedFilter)));
        }
      },
      () => dispatch(setFilterAction(cloneDeep(defaultFilterState)))
    );

    hydrateParamFromUrl(
      params, paramConfig.ui, lastAppliedRef, "ui", isHydrated,
      (urlUi) => {
        const parsedUi = isJsonParsable(urlUi);
        if (parsedUi) {
          const expandedUi = expandUiFromUrl(parsedUi, countryMeta);
          setUiState({ ...cloneDeep(initialUiState), ...expandedUi });
        }
      },
      () => setUiState(cloneDeep(initialUiState))
    );

    if (!isHydrated) {
      setIsHydrated(true);
    }
  }, [
    location.search,
    dispatch,
    setFilterAction,
    setUiState,
    initialUiState,
    defaultFilterState,
    paramConfig,
    isHydrated,
    countryMeta,
    isCountryMetaReady,
    scope,
  ]);

  const compressedFilter = useMemo(
    () => compressFilterForUrl(filterState, countryMeta, scope),
    [filterState, countryMeta, scope]
  );

  const compressedUi = useMemo(
    () => compressUiForUrl(uiState, countryMeta),
    [uiState, countryMeta]
  );

  const serializedFilter = useMemo(
    () => stringifySafe(compressedFilter),
    [compressedFilter]
  );

  const serializedUi = useMemo(() => stringifySafe(compressedUi), [compressedUi]);

  useEffect(() => {
    if (!paramConfig || !isHydrated) return;

    if (isStaleRender(location)) return;

    const params = new URLSearchParams(location.search);

    const filterChanged = syncParamToUrl(
      params, paramConfig.filter, filterState, serializedFilter,
      defaultFilterStringRef, lastAppliedRef, "filter"
    );
    const uiChanged = syncParamToUrl(
      params, paramConfig.ui, uiState, serializedUi,
      defaultUiStringRef, lastAppliedRef, "ui"
    );

    if (filterChanged || uiChanged) {
      pendingWriteRef.current = { key: location.key, search: location.search };
      setSearchParams(params, { replace: true });
    }
  }, [paramConfig, isHydrated, serializedFilter, serializedUi, location.search, filterState, uiState, setSearchParams]);
};
